import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";
// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import SaveIcon from "@mui/icons-material/Save";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// lib
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// components
import HUDForm from "components/Calculators/EscrowCalculator/components/HUDForm";
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import CashOfferCalculatorQuestions from "components/Calculators/CashOfferCalculator/components/CashOfferCalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";
import Authenticator from "components/Authenticator";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);

export default function CashOfferCalculator({
  URL,
  CapitalData,
  externalCalc,
  externalShowMeMyRate,
  plugInTheNumbers,
  setPlugInTheNumbers,
  setCalcs,
  dscrExampleCalcObj,
  calcs,
  loadCalcs,
  refCalcs,
  externalExampleCalc,
}) {
  const theme = useTheme();
  const router = useRouter();

  const [date, setDate] = useState(null);

  const { user, userProfile, isNewUser, setSnackbarData } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});

  const [externalCaclObjData, setExternalCalcObjData] = useState(
    dscrExampleCalcObj && Object.keys(dscrExampleCalcObj).length > 0
      ? dscrExampleCalcObj
      : {
          creditScore: 681,
          hoa: 211,
          insurance: 1254,
          price: 143000,
          rent: 1450,
          taxes: 988,
        }
  ); // this is default data for dscr-loan page where this custom obj isn't provided

  const [totalLoanLeft, setTotalLoanLeft] = useState(0);
  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly cashflow": true,
    "Cash to close": true,
  });
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({ cashOffer: 0 });

  const [buyerLTV, setBuyerLTV] = useState();

  const [loanPurpose, setLoanPurpose] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [monthlyPmt, setMonthlyPmt] = useState({});
  const [monthlyRent, setMonthlyRent] = useState(0);
  const [annualRent, setAnnualRent] = useState(0);
  const [annualTaxes, setAnnualTaxes] = useState(0);
  const [annualInsurance, setAnnualInsurance] = useState(0);
  const [annualHOA, setAnnualHOA] = useState(0);
  const [propertyValue, setPropertyValue] = useState(0);
  const [localInterestRate, setLocalInterestRate] = useState(0);
  const [LTV, setLTV] = useState(0);
  const [annualPropertyManagementExpense, setAnnualPropertyManagementExpense] =
    useState(0);
  const [dscr, setDSCR] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [cashToCloseIsPositive, setCashToCloseIsPositive] = useState(0);
  const [variableExpenseCashToClose, setVariableExpenseCashToClose] =
    useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [loanFees, setLoanFees] = useState(0);
  const [titleFees, setTitleFees] = useState(0);
  const [escrow, setEscrow] = useState(0);
  const [prepaidInterest, setPrepaidInterest] = useState(0);
  const [prepaidInsurance, setPrepaidInsurance] = useState(0);
  const [originationFee, setOriginationFee] = useState(0);
  const [estimatedAsIsValue, setEstimatedAsIsValue] = useState(0);
  const [existingDebt, setExistingDebt] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [changedKey, setChangedKey] = useState("");
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [localAddress, setLocalAddress] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  // const [showSaved, setShowSaved] = useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  // const [DSCRRateRange, setDSCRRateRange] = useState([]);
  const [current5yrTreasury, setCurrent5yrTreasury] = useState(null);
  const [personalizedIntSpread, setPersonalizedIntSpread] = useState(null);
  const [DSCRCutoff, setDSCRCutoff] = useState(null);
  const [creditScoreCutoff, setCreditScoreCutoff] = useState(null);
  const [creditScore, setCreditScore] = useState(null);
  const [rateUsed, setRateUsed] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "After repair value (ARV)",
    "Rehab budget",
    "Buyer strategy",
    "Wholesale fee",
  ];

  const thirdPart = ["Calculation name", "Address (optional)"];

  const showMeMyRatePart = ["Credit score"];

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  useEffect(() => {
    let updatedAnswers = [];
    let questions = CashOfferCalculatorQuestions;

    setQuestions(questions);

    // extract current DSCR interest rates from capital page's data
    // const DSCRRateRange = extractDSCRInterestRateRange(CapitalData);
    // setDSCRRateRange(DSCRRateRange);

    // if loaded on /my/calculations

    // where we preload existing/saved calcs
    if (externalCalc && externalCalc.Answers.length > 0) {
      // external calcs, can be 'new' calcs, that don't have answers, so other part of the if is relevant
      updatedAnswers = externalCalc.Answers.slice(0);

      updatedAnswers.forEach((answer) => {
        const [questionWithSameId] = questions.filter(
          (question) => question.q_id === answer.q_id
        );

        if (questionWithSameId) {
          // Update the answer with the corresponding question's properties
          answer.q_description = questionWithSameId.q_description;
          answer.q_title = questionWithSameId.q_title;
          answer.node_type = questionWithSameId.node_type;
          answer.a_type = questionWithSameId.a_type;
          answer.a_input_type = questionWithSameId.a_input_type;
          answer.a_opts = questionWithSameId.a_opts;
          answer.q_next_step_func = questionWithSameId.q_next_step_func;
          answer.q_validation_func = questionWithSameId.q_validation_func;
          answer.q_invalid_answer_help_text =
            questionWithSameId.q_invalid_answer_help_text;
          answer.a_error = questionWithSameId.a_error;
        }
      });

      setAnswers(updatedAnswers);

      setShowUpdateFields(true);
    } else {
      let q_id_counter = "";
      let answers = [];
      const questionsSlice = questions.slice(); // make sure to make a copy
      let leftMostPath = questionsSlice.filter((question) => {
        // https://stackoverflow.com/a/21596128/1933331
        return /^0*$/.test(question.q_id);
      });

      // leftMostPath
      // must sort based on q_id length of 0s inside
      // left most paths q_ids
      leftMostPath.sort((a, b) => a.q_id.length - b.q_id.length);

      // leftMostPath for initial state
      // as user selects answers,
      // the path deviates and questions ahead change
      // this populates empty placeholder answers from initial questions
      for (let i = 0; i < leftMostPath.length; i++) {
        const question = leftMostPath[i];
        q_id_counter = q_id_counter + "0";
        answers.push({
          a_string: "",
          q_id: new Array(i + 2).join("0"),
          q_description: question.q_description,
          q_title: question.q_title,
          node_type: question.node_type,
          a_type: question.a_type,
          a_input_type: question.a_input_type,
          a_opts: question.a_opts,
          q_next_step_func: question.q_next_step_func,
          q_validation_func: question.q_validation_func,
          q_invalid_answer_help_text: question.q_invalid_answer_help_text,
          a_error: question.a_error,
        });
      }

      // TODO update this when user is logged in, during later development of the calculator
      // const updatedAnswers = excludeAccountQuestionIfLoggedIn(
      //   user,
      //   userProfile,
      //   answers
      // );

      updatedAnswers = answers;
    }

    setAnswers(updatedAnswers);
  }, []);

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  useEffect(() => {
    const arv =
      answers?.filter((answer) => {
        return answer?.q_title === "After repair value (ARV)";
      })?.[0]?.a_string || 0;

    const rehabBudget =
      answers?.filter((answer) => {
        return answer?.q_title === "Rehab budget";
      })?.[0]?.a_string || 0;

    const buyerStrategy = answers?.filter((answer) => {
      return answer?.q_title === "Buyer strategy";
    })?.[0]?.a_string;

    const wholesaleFee =
      answers?.filter((answer) => {
        return answer?.q_title === "Wholesale fee";
      })?.[0]?.a_string || 0;

    const buyerLTV = buyerStrategy ? buyerStrategy.split("%")[0] : 0;

    setBuyerLTV(buyerLTV);

    const arvFraction = parseFloat(arv) * (parseFloat(buyerLTV) / 100);

    const cashOffer = Math.round(
      parseFloat(arvFraction) -
        parseFloat(rehabBudget) -
        parseFloat(wholesaleFee)
    );

    const uCurrentValues = Object.assign({}, currentValues);

    uCurrentValues["cashOffer"] = cashOffer;

    setCurrentValues(uCurrentValues);
  }, [answers, totalLoanLeft]);

  // useEffect(() => {
  //   if (loanQuoteData && Object.keys(loanQuoteData).length > 0) {
  //     setCurrent5yrTreasury(parseFloat(loanQuoteData?.treasury5yr?.rate));

  //     const ltv = answers?.filter((answer) => {
  //       return answer?.q_title === "Loan to value (LTV)";
  //     })?.[0]?.a_string;

  //     // find closes integer key in the LTVObj
  //     // however ltv might not be an exact number that matches one of the keys
  //     // then use that to get the personalized interest rate spread
  //     const ltvKeys = Object.keys(loanQuoteData?.LTVObj);

  //     ltvKeys.forEach((key, index) => {
  //       if (
  //         parseInt(ltv) >= parseInt(key) &&
  //         ltvKeys[index + 1] &&
  //         parseInt(ltv) <= parseInt(ltvKeys[index + 1])
  //       ) {
  //         setPersonalizedIntSpread(loanQuoteData?.LTVObj[key]?.creditSpread);
  //         setDSCRCutoff(loanQuoteData?.LTVObj[key]?.DSCRCutoff);
  //         setCreditScoreCutoff(loanQuoteData?.LTVObj[key]?.creditScoreCutoff);
  //       }
  //     });

  //     if (plugInTheNumbers) {
  //       const updatedAnswers = answers.slice(0);

  //       const topLTV = ltvKeys[ltvKeys.length - 1];
  //       const ltvRange = loanQuoteData?.LTVObj[topLTV];

  //       const intRate = (
  //         parseFloat(loanQuoteData.treasury5yr.rate) +
  //         parseFloat(ltvRange.creditSpread)
  //       ).toFixed(2);

  //       const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
  //         return answer?.q_title === "Loan to value (LTV)";
  //       });

  //       toUpdateAnswer.a_string = topLTV;

  //       const [toUpdateAnswerInterestRate] = updatedAnswers.filter((answer) => {
  //         return answer?.q_title === "Interest rate";
  //       });

  //       toUpdateAnswerInterestRate.a_string = intRate;

  //       // loanFees
  //       // purchase price
  //       externalCaclObjData.loanAmount = externalCaclObjData.price * 0.7;
  //       externalCaclObjData.loanFees = externalCaclObjData.loanAmount * 0.03;

  //       // plug in Cacl example
  //       const plugInCalcAnswers = [
  //         {
  //           q_id: "0",
  //           a_type: "mc",
  //           a_error: false,
  //           q_title: "Loan purpose",
  //           a_string: "Purchase",
  //           node_type: "node",
  //           a_input_type: "",
  //           q_invalid_answer_help_text: "",
  //         },
  //         {
  //           q_id: "00",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Purchase price",
  //           a_string: externalCaclObjData.price,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a dollar amount above 0",
  //         },
  //         {
  //           q_id: "00000",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Loan fees",
  //           a_string: externalCaclObjData.loanFees,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a value above 0",
  //         },
  //         {
  //           q_id: "000000",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Monthly rent",
  //           a_string: externalCaclObjData.rent,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a value above 0",
  //         },
  //         {
  //           q_id: "0000000",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Annual taxes",
  //           a_string: externalCaclObjData.taxes,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a value above 0",
  //         },
  //         {
  //           q_id: "00000000",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Annual insurance",
  //           a_string: externalCaclObjData.insurance,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a value above 0",
  //         },
  //         {
  //           q_id: "000000000",
  //           a_type: "currency",
  //           a_error: false,
  //           q_title: "Annual HOA",
  //           a_string: externalCaclObjData.hoa,
  //           node_type: "node",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text: "Please enter a value above 0",
  //         },
  //         {
  //           q_id: "0000000000",
  //           a_type: "text",
  //           a_error: false,
  //           q_title: "Calculation name",
  //           a_string: "Plug in example",
  //           node_type: "node",
  //           a_input_type: "text",
  //           q_invalid_answer_help_text: "",
  //         },
  //         {
  //           q_id: "00000000000",
  //           a_type: "address",
  //           a_error: false,
  //           q_title: "Address (optional)",
  //           a_string: "",
  //           node_type: "node",
  //           q_invalid_answer_help_text: "Please enter or select an address",
  //         },

  //         {
  //           q_id: "0000000000000",
  //           a_type: "mc",
  //           a_error: false,
  //           q_title: "Refinance type",
  //           a_string: "Cash out refinance",
  //           node_type: "leaf",
  //           a_input_type: "",
  //           q_invalid_answer_help_text: "",
  //         },
  //         {
  //           q_id: "00000",
  //           q_title: "Loan amount",
  //           a_string: externalCaclObjData.loanAmount,
  //           node_type: "node",
  //           a_type: "currency",
  //           a_input_type: "number",
  //         },
  //         {
  //           q_id: "00000000",
  //           q_title: "Loan to value (LTV)",
  //           a_string:
  //             (externalCaclObjData.loanAmount / externalCaclObjData.price) *
  //             100,
  //           node_type: "node",
  //           a_type: "percent",
  //           a_input_type: "number",
  //           q_invalid_answer_help_text:
  //             "Please enter a value between 50 and 90 (a common range of offered LTVs)",
  //           a_error: false,
  //         },
  //       ];

  //       plugInCalcAnswers.forEach((answer) => {
  //         const [toUpdate] = updatedAnswers.filter((updatedAnswer) => {
  //           return updatedAnswer.q_title === answer.q_title;
  //         });
  //         // toUpdate
  //         if (toUpdate) {
  //           toUpdate.a_string = answer.a_string;
  //         }
  //       });

  //       setAnswers(updatedAnswers);
  //       // setCalc(plugInCalc);
  //       // setShowUpdateFields(true);
  //       setShowSaveFields(true);
  //     }
  //   }
  // }, [loanQuoteData]);

  // useEffect(() => {
  //   if (externalShowMeMyRate !== false && externalShowMeMyRate) {
  //     setShowMeMyRate(externalShowMeMyRate);
  //   }

  //   if (externalShowMeMyRate === false) {
  //     setShowMeMyRate(externalShowMeMyRate);
  //   }
  // }, [externalShowMeMyRate]);

  // useEffect(() => {
  //   // TODO get this working for cash offer calculator

  //   // this can be done on a listing page
  //   if (plugInTheNumbers) {
  //     const updatedAnswers = answers.slice(0);

  //     const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
  //       return answer?.q_title === "Loan purpose";
  //     });

  //     if (!toUpdateAnswer.a_string) {
  //       // just default to purchase for ease for now
  //       toUpdateAnswer.a_string = "Purchase";
  //     }

  //     // Credit score from example harcoded at 681
  //     const [toUpdateAnswerCreditScore] = updatedAnswers.filter((answer) => {
  //       return answer?.q_title === "Credit score";
  //     });

  //     toUpdateAnswerCreditScore.a_string = externalCaclObjData.creditScore;

  //     setAnswers(updatedAnswers);

  //     // now get the tiers, no longer need LTV, will just show all LTVs even if its set
  //     // to show wider range of options
  //     (async () => {
  //       await getDSCRLTVTiers(updatedAnswers, setLoanQuoteData);
  //     })();
  //   }
  // }, [plugInTheNumbers]);

  const processSelect = (ltvRange) => {
    const updatedAnswers = answers.slice(0);

    const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Loan to value (LTV)";
    });

    toUpdateAnswer.a_string = ltvRange.LTV;

    const [toUpdateAnswerInterestRate] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Interest rate";
    });

    toUpdateAnswerInterestRate.a_string = (
      parseFloat(loanQuoteData.treasury5yr.rate) +
      parseFloat(ltvRange.creditSpread)
    ).toFixed(2);

    // clear interest rate in current values so value shows in textField
    const updatedCurrentValues = Object.assign({}, currentValues);

    // reset all current values
    Object.keys(updatedCurrentValues).forEach((key) => {
      updatedCurrentValues[key] = undefined;
    });

    setCurrentValues(updatedCurrentValues);

    // set answers last to recalc
    setAnswers(updatedAnswers);

    setTimeout(() => {
      scroller(`text_field_Purchase price`);
      setShowMeMyRate(false);
    }, 1000);
  };

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();

    const updatedAnswers = answers.slice(0);

    const isValidAnswer = answer.q_validation_func(value, answers);

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // save users answer
      targetAnswer.a_string = value;

      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      // const next_q_id = answer.q_next_step_func(targetAnswer, answers);
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like using slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    const questionsSlice = questions.slice();

    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      answer.a_string === a_opt ? "contained" : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title])
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              label={answer.q_title || ""}
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric", // This makes mobile to pull up numbers only keyboard
              }}
              value={uiValue || ""}
              // InputLabelProps={{
              //   shrink: uiValue,
              // }}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");

                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"First payment"}
                    value={date ? dayjs(date) : null}
                    onChange={(newDate) => {
                      setDate(newDate);
                    }}
                    onBlur={(newDate) => {
                      processSubmission(newDate);
                    }}
                    format="MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      case "escrowRow":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "row",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                [theme.breakpoints.down("md")]: {
                  flexDirection: "column",
                },
                gap: theme.spacing(1),
              }}
            >
              <TextField
                id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
                helperText={
                  answer.a_error
                    ? answer.q_invalid_answer_help_text
                    : answer.q_help_text
                    ? answer.q_help_text
                    : ""
                }
                error={answer.a_error}
                label={answer.a_error ? "Error" : "Amount"}
                type={answer.a_type_input}
                value={
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title]
                }
                InputLabelProps={{
                  shrink:
                    currentValues[answer.q_title] === undefined
                      ? answer.a_string
                        ? answer.a_string
                        : null
                      : currentValues[answer.q_title],
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const updatedCurrentValues = Object.assign({}, currentValues);
                  updatedCurrentValues[answer.q_title] = value;
                  setCurrentValues(updatedCurrentValues);
                  handleUserSelection(
                    value,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );

                  // this is done to reset
                  // 'get rate' output when new stuff is typed in
                  setLoanQuoteData({});
                }}
              />
              <FormControl
                sx={{
                  minWidth: "20%",
                }}
              >
                <InputLabel id={`dropdown_label_${answer.q_title}`}>
                  Cushion
                </InputLabel>
                <Select
                  labelId={`dropdown_label_${answer.q_title}`}
                  id={`dropdown_${answer.q_title}`}
                  value={
                    currentValues[answer.q_title] === undefined
                      ? answer.a_string
                        ? answer.a_string
                        : null
                      : currentValues[answer.q_title]
                  }
                  label={answer.q_title}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updatedCurrentValues = Object.assign(
                      {},
                      currentValues
                    );
                    updatedCurrentValues[answer.q_title] = value;
                    setCurrentValues(updatedCurrentValues);
                    handleUserSelection(
                      value,
                      answer,
                      answers,
                      questions,
                      setAnswers
                    );

                    // this is done to reset
                    // 'get rate' output when new stuff is typed in
                    //   setLoanQuoteData({});
                  }}
                >
                  <MenuItem value={0}>0</MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={["month", "year"]}
                  format="MM/YYYY"
                  label={`First payment`}
                  value={date ? dayjs(date) : null}
                  onChange={(newDate) => {
                    setDate(newDate);
                  }}
                  onBlur={(newDate) => {
                    processSubmission(newDate);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const showError =
    loanQuoteData?.LTVObj &&
    (Object.keys(loanQuoteData.LTVObj).length === 0 ||
      creditScore <
        loanQuoteData.LTVObj[Object.keys(loanQuoteData.LTVObj)[0]]
          .creditScoreCutoff);

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}

      {answers.length > 0 ? (
        <Box>
          <Box>
            {answers
              .filter((answer) => {
                // filtering out the question that we don't want to show in this part of the UI
                return firstPartOfQuestions.includes(answer.q_title);
              })
              .map((answer, index) => {
                return (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: theme.spacing(0.5),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: "1 1 80%",
                          display: "flex",
                          flexDirection: "row",
                          [theme.breakpoints.down("md")]: {
                            flex: "0 0 50%",
                          },
                        }}
                      >
                        <ButtonBase
                          sx={{
                            width: "100%",
                            padding: theme.spacing(1),
                            "&:focus": {
                              outline: "none",
                              borderRadius: theme.spacing(0.5),
                              boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                                theme.palette.info.main
                              }`,
                            },
                            [theme.breakpoints.down("md")]: {
                              margin: theme.spacing(1),
                              padding: 0,
                            },
                          }}
                          onClick={() => {
                            let updatedShowQuestionDescription = Object.assign(
                              {},
                              showQuestionDescription
                            );

                            updatedShowQuestionDescription[
                              `${answer.q_title}`
                            ] =
                              !updatedShowQuestionDescription[
                                `${answer.q_title}`
                              ];

                            setShowQuestionDescription(
                              updatedShowQuestionDescription
                            );
                          }}
                        >
                          <Typography
                            variant="h2"
                            sx={{
                              fontSize: theme.spacing(3),
                              textAlign: "left",
                              [theme.breakpoints.down("md")]: {
                                fontSize: theme.spacing(2),
                              },
                            }}
                          >
                            {answer.q_title}
                          </Typography>
                          {showQuestionDescription[`${answer.q_title}`] ? (
                            <KeyboardArrowUpIcon
                              sx={{
                                display: "flex",
                                margin: theme.spacing(
                                  "auto",
                                  1,
                                  "auto",
                                  "auto"
                                ),
                                fontSize: theme.spacing(2.5),
                              }}
                            />
                          ) : (
                            <InfoOutlinedIcon
                              sx={{
                                display: "flex",
                                margin: theme.spacing(
                                  "auto",
                                  1,
                                  "auto",
                                  "auto"
                                ),
                                fontSize: theme.spacing(2.5),
                              }}
                              color="info"
                            />
                          )}
                        </ButtonBase>
                      </Box>
                      <Box
                        sx={{
                          displat: "flex",
                          flex: "1 1 65%",
                          [theme.breakpoints.down("md")]: {
                            flex: "0 0 50%",
                          },
                        }}
                      >
                        {generateAnswerOptions(answer, index, answers)}
                      </Box>
                    </Box>
                    <Collapse in={showQuestionDescription[`${answer.q_title}`]}>
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            margin: theme.spacing(1),
                            fontSize: theme.spacing(1.8),
                            [theme.breakpoints.down("md")]: {
                              margin: theme.spacing(1, 0, 1, 1),
                            },
                          }}
                        >
                          {answer?.q_description(answers)}
                        </Typography>
                      </Box>
                    </Collapse>
                    {answer.q_title === "Interest rate" && (
                      <>
                        <Alert
                          sx={{
                            width: "100%",
                            display: "flex",
                            margin: theme.spacing(1, "auto"),
                            "& .MuiAlert-message": {
                              width: "100% !important",
                            },
                            [theme.breakpoints.down("md")]: {
                              "& .MuiAlert-icon": {
                                display: "none",
                              },
                            },
                          }}
                          severity="info"
                          variant="outlined"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-around",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                margin: theme.spacing("auto", 0),
                              }}
                            >
                              <Typography
                                variant="h2"
                                sx={{
                                  fontSize: theme.spacing(2.5),
                                }}
                              >
                                What's my rate?
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                [theme.breakpoints.down("md")]: {},
                              }}
                            >
                              <Button
                                color="info"
                                variant={"contained"}
                                sx={{
                                  textDecoration: "none !important",
                                  color: "white !important",
                                  margin: theme.spacing(1.5, 0, 0, 0),
                                  "&:hover": {
                                    color: `${theme.palette.info.contrastColor} !important`,
                                    backgroundColor: `${theme.palette.info.dark} !important`,
                                  },
                                  [theme.breakpoints.down("md")]: {
                                    width: "auto",
                                  },
                                }}
                                onClick={() => {
                                  setShowMeMyRate(!showMeMyRate);
                                }}
                              >
                                Show me my rate
                              </Button>
                              <Typography
                                component="h2"
                                sx={{
                                  display: "flex",
                                  width: "auto",
                                  fontSize: theme.spacing(1.5),
                                  textAlign: "right",
                                  [theme.breakpoints.down("md")]: {
                                    display: "block",
                                    margin: 0,
                                  },
                                }}
                              >
                                *1 additional question, no credit pull
                              </Typography>
                            </Box>
                          </Box>

                          {showMeMyRate && (
                            <Box sx={{ width: "100% !important" }}>
                              <>
                                {answers
                                  .filter((answer) => {
                                    // filtering out the question that we don't want to show in this part of the UI
                                    return showMeMyRatePart.includes(
                                      answer.q_title
                                    );
                                  })
                                  .map((answer, index) => {
                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "row",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              flex: "0 1 50%",

                                              display: "flex",
                                              flexDirection: "row",
                                            }}
                                          >
                                            <ButtonBase
                                              sx={{
                                                width: "100%",
                                                padding: theme.spacing(1),
                                                "&:focus": {
                                                  outline: "none",
                                                  borderRadius:
                                                    theme.spacing(0.5),
                                                  boxShadow: `0 0 0 ${theme.spacing(
                                                    0.5
                                                  )} ${
                                                    theme.palette.info.main
                                                  }`,
                                                },
                                                [theme.breakpoints.down("md")]:
                                                  {
                                                    margin: theme.spacing(1),
                                                    padding: 0,
                                                  },
                                              }}
                                              onClick={() => {
                                                let updatedShowQuestionDescription =
                                                  Object.assign(
                                                    {},
                                                    showQuestionDescription
                                                  );

                                                updatedShowQuestionDescription[
                                                  `${answer.q_title}`
                                                ] =
                                                  !updatedShowQuestionDescription[
                                                    `${answer.q_title}`
                                                  ];

                                                setShowQuestionDescription(
                                                  updatedShowQuestionDescription
                                                );
                                              }}
                                            >
                                              <Typography
                                                variant="h2"
                                                sx={{
                                                  fontSize: theme.spacing(3),
                                                  textAlign: "left",
                                                  [theme.breakpoints.down(
                                                    "md"
                                                  )]: {
                                                    fontSize: theme.spacing(2),
                                                  },
                                                }}
                                              >
                                                {answer.q_title}
                                              </Typography>
                                              {showQuestionDescription[
                                                `${answer.q_title}`
                                              ] ? (
                                                <KeyboardArrowUpIcon
                                                  sx={{
                                                    display: "flex",
                                                    margin: theme.spacing(
                                                      "auto",
                                                      1,
                                                      "auto",
                                                      "auto"
                                                    ),
                                                    fontSize:
                                                      theme.spacing(2.5),
                                                  }}
                                                />
                                              ) : (
                                                <InfoOutlinedIcon
                                                  sx={{
                                                    display: "flex",
                                                    margin: theme.spacing(
                                                      "auto",
                                                      1,
                                                      "auto",
                                                      "auto"
                                                    ),
                                                    fontSize:
                                                      theme.spacing(2.5),
                                                  }}
                                                  color="info"
                                                />
                                              )}
                                            </ButtonBase>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flex: "0 1 50%",
                                            }}
                                          >
                                            {generateAnswerOptions(
                                              answer,
                                              index,
                                              answers
                                            )}
                                          </Box>
                                        </Box>
                                        <Collapse
                                          in={
                                            showQuestionDescription[
                                              `${answer.q_title}`
                                            ]
                                          }
                                        >
                                          <Box sx={{ width: "100%" }}>
                                            <Typography
                                              variant="subtitle1"
                                              sx={{
                                                margin: theme.spacing(1),
                                                fontSize: theme.spacing(1.8),
                                                [theme.breakpoints.down("md")]:
                                                  {
                                                    margin: theme.spacing(
                                                      1,
                                                      0,
                                                      1,
                                                      1
                                                    ),
                                                  },
                                              }}
                                            >
                                              {answer.q_description(answers)}
                                            </Typography>
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    );
                                  })}
                                {userProfile &&
                                Object.keys(userProfile).length === 0 ? (
                                  <Box
                                    sx={{
                                      margin: theme.spacing(
                                        3,
                                        "auto",
                                        5,
                                        "auto"
                                      ),
                                      width: "66%",
                                      [theme.breakpoints.down("md")]: {
                                        width: "100%",
                                      },
                                    }}
                                  >
                                    <Authenticator />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      padding: theme.spacing(1),
                                      border: `none`,
                                      borderRadius: `none`,
                                      borderTop: `none`,
                                      borderTopRightRadius: 0,
                                      borderTopLeftRadius: 0,
                                      margin: "none",
                                      "& .MuiFormControl-root": {
                                        margin: theme.spacing(1, 0, 0, "auto"),
                                        display: "flex",
                                        width: "100%",
                                      },
                                    }}
                                  >
                                    {showError && (
                                      <Alert
                                        severity="error"
                                        variant={"outlined"}
                                        sx={{ margin: theme.spacing(1) }}
                                      >
                                        Please increase credit score
                                      </Alert>
                                    )}

                                    <Box
                                      sx={{
                                        margin: 0,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: "100%",
                                          }}
                                        ></Box>
                                        <Box
                                          sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "row",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              flex: "0 1 50%",
                                              display: "flex",
                                              flexDirection: "row",
                                            }}
                                          >
                                            {/* need this empty box for positioning */}
                                          </Box>
                                          <Box
                                            sx={{
                                              fontWeight: 600,
                                              textAlign: "center",
                                              flex: "0 1 50%",
                                            }}
                                          >
                                            <Button
                                              sx={{
                                                margin: theme.spacing("auto"),
                                              }}
                                              color="info"
                                              variant={"contained"}
                                              onClick={() => {
                                                (async () => {
                                                  // check if loan type is set
                                                  // if not, set it to default purchase
                                                  const updatedAnswers =
                                                    answers.slice(0);
                                                  const [toUpdateAnswer] =
                                                    updatedAnswers.filter(
                                                      (answer) => {
                                                        return (
                                                          answer?.q_title ===
                                                          "Loan purpose"
                                                        );
                                                      }
                                                    );

                                                  if (
                                                    !toUpdateAnswer.a_string
                                                  ) {
                                                    // just default to purchase for ease for now
                                                    toUpdateAnswer.a_string =
                                                      "Purchase";
                                                  }

                                                  setAnswers(updatedAnswers);

                                                  // now get the tiers, no longer need LTV, will just show all LTVs even if its set
                                                  // to show wider range of options
                                                  await getDSCRLTVTiers(
                                                    answers,
                                                    setLoanQuoteData
                                                  );
                                                  setRateUsed(false);
                                                })();
                                              }}
                                            >
                                              Get my rate
                                            </Button>
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {!showError &&
                                      Object.keys(loanQuoteData).length > 0 && (
                                        <>
                                          <Box
                                            sx={{
                                              margin: theme.spacing(2, 0, 0, 0),
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                }}
                                              >
                                                <Typography
                                                  variant="h2"
                                                  sx={{
                                                    fontSize: theme.spacing(3),
                                                    margin: `0 !important`,
                                                  }}
                                                >
                                                  Your rate
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  display: "flex",
                                                  flexDirection: "row",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    flex: "0 1 100%",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                  }}
                                                >
                                                  <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                      textAlign: "left",
                                                      fontSize:
                                                        theme.spacing(1.8),
                                                    }}
                                                  >
                                                    The higher your credit score
                                                    is the better interest rate
                                                    we can offer.
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Box>

                                          <Box
                                            sx={{
                                              margin: theme.spacing(3, 0, 0, 0),
                                            }}
                                          >
                                            <SpreadLTVTable
                                              spreadQuoteData={loanQuoteData}
                                              processSelect={processSelect}
                                            />
                                          </Box>

                                          <Alert
                                            severity={
                                              showError ? "error" : "info"
                                            }
                                            variant={"outlined"}
                                            sx={{ margin: theme.spacing(1) }}
                                          >
                                            {showError ? (
                                              <>Please increase credit score</>
                                            ) : (
                                              <>
                                                Pressing SELECT will fill out
                                                the DSCR Calculator above so you
                                                can proceed with your
                                                calculation.
                                              </>
                                            )}
                                          </Alert>
                                        </>
                                      )}
                                    {router.pathname.includes(
                                      "/escrow-calculator" // only show this button on DSCR-CALCULATOR PAGE FOR NOW
                                    ) && (
                                      <Box
                                        sx={{
                                          margin: theme.spacing("auto"),
                                          display: "flex",
                                          flexDirection: "column",
                                          [theme.breakpoints.down("md")]: {},
                                        }}
                                      >
                                        <Button
                                          color="success"
                                          variant={"contained"}
                                          sx={{
                                            textDecoration: "none !important",
                                            color: "white !important",
                                            margin: theme.spacing(1.5, 0, 0, 0),
                                            "&:hover": {
                                              color: `${theme.palette.primary.contrastColor} !important`,
                                              backgroundColor: `${theme.palette.success.dark} !important`,
                                            },
                                            [theme.breakpoints.down("md")]: {
                                              width: "auto",
                                            },
                                          }}
                                          href={
                                            "/loans?ref=escrow-calculator-btn"
                                          }
                                          onClick={() => {
                                            gtag.event({
                                              action:
                                                "dscr_calc_to_quoter_navigation",
                                            });
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              margin: theme.spacing(0, 1),
                                              fontSize: theme.spacing(2),
                                            }}
                                          >
                                            {" "}
                                            ⚡
                                          </Box>
                                          Instant quote{" "}
                                        </Button>
                                        <Typography
                                          component="h2"
                                          sx={{
                                            display: "flex",
                                            fontSize: theme.spacing(1.5),
                                            margin: theme.spacing(
                                              0,
                                              0,
                                              0,
                                              "auto"
                                            ),
                                            [theme.breakpoints.down("md")]: {
                                              display: "block",
                                            },
                                          }}
                                        >
                                          *takes 2 minutes, no credit pull
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </>
                            </Box>
                          )}
                        </Alert>
                      </>
                    )}
                  </Box>
                );
              })}
          </Box>

          <Box
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 3),
              borderRadius: theme.spacing(1),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box
              id="title_Cash offer"
              tabindex={0}
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                padding: theme.spacing(3),
                border: `1px solid ${
                  monthlyCashflow > -1
                    ? theme.palette.success.main
                    : theme.palette.primary.main
                }`,
                borderRadius: theme.spacing(1),
                "&:focus": {
                  outline: "none",
                  borderRadius: theme.spacing(0.5),
                  boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                    theme.palette.success.main
                  }`,
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );

                        updatedShowQuestionDescription[`Monthly cashflow`] =
                          !updatedShowQuestionDescription[`Monthly cashflow`];

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 600,
                          fontSize: theme.spacing(3),
                          textAlign: "left",
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2),
                          },
                        }}
                      >
                        Cash offer
                      </Typography>
                      {showQuestionDescription[`Monthly cashflow`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>

                  <Box
                    sx={{
                      fontWeight: 600,
                      textAlign: "center",
                      flex: "0 1 50%",
                      margin: "auto",
                      color:
                        currentValues["cashOffer"] > -1
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                    }}
                  >
                    {`$${formatMoney(currentValues["cashOffer"], 0)}
                      `}{" "}
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Monthly cashflow`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 2),
                        },
                      }}
                    >
                      {`$${formatMoney(
                        parseInt(currentValues["After repair value (ARV)"]),
                        0
                      )}`}{" "}
                      ARV
                      <br />* {`${buyerLTV}%`} Buyer strategy (LTV percentage)
                      <br />-{" "}
                      {`$${formatMoney(
                        parseInt(currentValues["Rehab budget"]),
                        0
                      )}`}{" "}
                      Rehab budget
                      <br />-{" "}
                      {`$${formatMoney(
                        parseInt(currentValues["Wholesale fee"]),
                        0
                      )}`}{" "}
                      Wholesale fee
                      <br /> ={" "}
                      {`$${formatMoney(currentValues["cashOffer"], 0)}
                      `}{" "}
                      Maximum estimated cash offer
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>

            {!router.pathname.includes("/my/calculation") && (
              <Alert
                sx={{
                  margin: theme.spacing(2, 0, 0, 0),
                  width: "100%",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
                severity={"info"}
                variant="outlined"
              >
                <>
                  <b>
                    Would you like to save your progress or share your
                    calculation?
                  </b>
                  <br />
                  <br />
                  All your calculations will be stored in{" "}
                  <Link
                    sx={{ color: theme.palette.highContrastLink.main }}
                    rel="noopener"
                    target="_blank"
                    href="/my/calculations"
                  >
                    Calculations
                  </Link>
                </>

                <Box sx={{ display: "flex" }}>
                  <Button
                    color={showSaveFields ? "primary" : "info"}
                    variant={"contained"}
                    sx={{
                      width: "45%",
                      fontWeight: 600,
                      display: "flex",
                      textDecoration: "none !important",
                      color: "white !important",
                      margin: theme.spacing(3, 0, 0, "auto"),
                      "&:hover": {
                        color: `${theme.palette.primary.contrastColor} !important`,
                        backgroundColor: `${theme.palette.success.dark} !important`,
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "auto",
                      },
                    }}
                    onClick={() => {
                      setShowSaveFields(!showSaveFields);
                    }}
                  >
                    {showSaveFields ? (
                      <Box
                        sx={{
                          padding: theme.spacing(0.5),
                        }}
                      >
                        Cancel
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "90%",
                          [theme.breakpoints.down("md")]: {
                            width: "auto",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            padding: theme.spacing(0.5),
                          }}
                        >
                          {" "}
                          <SaveIcon /> Save
                        </Box>
                        <Box
                          sx={{
                            padding: theme.spacing(0.5),
                          }}
                        >
                          |
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            padding: theme.spacing(0.5),
                          }}
                        >
                          {" "}
                          <ShareOutlinedIcon /> Share
                        </Box>
                      </Box>
                    )}
                  </Button>
                </Box>
              </Alert>
            )}
            <Collapse
              in={router.pathname.includes("/my/calculation") || showSaveFields}
              mountOnEnter
              unmountOnExit
            >
              <Box
                sx={{
                  margin: theme.spacing(3, "auto", 0, "auto"),
                  width: "100%",
                }}
              >
                {userProfile && Object.keys(userProfile).length === 0 && (
                  <Box
                    sx={{
                      margin: theme.spacing(3, "auto", 5, "auto"),
                      width: "66%",
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                      },
                    }}
                  >
                    <Authenticator
                      customTitle={
                        "Sign up or log in to save, share or download"
                      }
                    />
                  </Box>
                )}
                {userProfile && Object.keys(userProfile).length > 0 && (
                  <Box>
                    {(router.pathname.includes("/my/calculation") ||
                      showSaveFields) &&
                      answers
                        .filter((answer) => {
                          // filtering out the question that we don't want to show in this part of the UI

                          return thirdPart.includes(answer.q_title);
                        })
                        .map((answer, index) => {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                padding: theme.spacing(1),
                                borderTop: `none`,
                                borderTopRightRadius: 0,
                                borderTopLeftRadius: 0,
                                "& .MuiFormControl-root": {
                                  margin: theme.spacing(1, 0, 0, "auto"),
                                  display: "flex",
                                  width: "100%",
                                },
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <Typography
                                  variant="h2"
                                  sx={{ fontSize: theme.spacing(3) }}
                                >
                                  {answer.q_title}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: "0 1 50%",

                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      margin: theme.spacing("auto", 0),
                                      fontSize: theme.spacing(1.8),
                                    }}
                                  >
                                    {answer.q_description(answers)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    displat: "flex",
                                    flex: "1 1 50%",
                                  }}
                                >
                                  {generateAnswerOptions(
                                    answer,
                                    index,
                                    answers
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: theme.spacing(1),
                        "& .MuiFormControl-root": {
                          margin: theme.spacing(1, 0, 0, "auto"),
                          display: "flex",
                          width: "100%",
                        },
                      }}
                    >
                      <Button
                        id={"DSCRCalcSaveButton"}
                        onClick={() => {
                          const targetUserID = userProfile.UID;
                          const calcType = "Cash offer";

                          (async () => {
                            // before we create or update we need to add to answers
                            // some of the generated responses like loan amount
                            const calcData = {
                              "Loan amount": loanAmount,
                              "Monthly payment": monthlyPmt,
                              DSCR: dscr,
                              "Cash to close": cashToClose,
                              "Monthly cashflow": monthlyCashflow,
                            };

                            let updatedCalc;
                            if (!externalCalc) {
                              updatedCalc = await createCalc(
                                user,
                                targetUserID,
                                answers,
                                calcType,
                                calcData,
                                true
                              );
                              // send an email to the user
                              // with their calc details
                              // so they have a way to return to it
                              // this is done on the backend now, if query param l=1 is passed to createCalc
                            } else {
                              const name = answers?.filter((answer) => {
                                return answer?.q_title === "Calculation name";
                              })?.[0]?.a_string;

                              const address = answers?.filter((answer) => {
                                return answer?.q_title === "Address (optional)";
                              })?.[0]?.a_string;

                              updatedCalc = await updateCalc(
                                user,
                                externalCalc,
                                {
                                  Answers: answers,
                                  CalcData: calcData,
                                  Name: name,
                                  Address: address,
                                }
                              );
                            }

                            setCalc(updatedCalc);

                            if (!showUpdateFields) {
                              setShowCreatedSuccessMessage(true);
                            }

                            setSnackbarData({
                              message: "Calculation saved",
                              options: {
                                variant: "success",
                                autoHideDuration: 5000,
                                anchorOrigin: {
                                  vertical: "top",
                                  horizontal: "center",
                                },
                              },
                            });

                            gtag.event({ action: "dscr_calc_saved" });
                          })();
                        }}
                        color="success"
                        variant={"contained"}
                        sx={{
                          margin: theme.spacing(0, 0, 0, "auto"),
                          fontWeight: 600,
                          display: "flex",
                          width: "40%",

                          "&:hover": {
                            color: `${theme.palette.primary.contrastColor} !important`,
                            backgroundColor: `${theme.palette.success.dark} !important`,
                          },
                          [theme.breakpoints.down("md")]: {
                            width: "auto",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            padding: theme.spacing(1),
                          }}
                        >
                          {" "}
                          <SaveIcon />
                        </Box>

                        {!calc ? "Save" : "Update"}
                      </Button>
                      {showCreatedSuccessMessage && (
                        <Alert sx={{ margin: theme.spacing(1) }}>
                          Your calculation has been saved!
                          <br />
                          <br />
                          To update or compare your calculations, go to{" "}
                          <a href="/my/calculations">Calculations</a>{" "}
                        </Alert>
                      )}

                      <Alert
                        variant="outlined"
                        icon={
                          <Box>
                            <ShareOutlinedIcon size="lg" />
                          </Box>
                        }
                        sx={{
                          width: "100% !important",
                          "& .MuiAlert-message": {
                            width: "100% !important",
                          },
                          display: "flex",
                          margin: theme.spacing(3, 0, 1, 0),
                          color: "black !important",
                        }}
                        color={"info"}
                      >
                        <Box
                          sx={{
                            textAlign: "left",
                            fontSize: theme.spacing(3),
                            color: theme.palette.info.main,
                            display: "flex",
                          }}
                        >
                          Share your calculation{" "}
                        </Box>
                        <Box>
                          <TextField
                            sx={{
                              textAlign: "left",
                              width: "100%",
                              margin: theme.spacing(2, 0),
                            }}
                            label={
                              calc?.RefTag
                                ? "Share URL"
                                : "Press SAVE above to generate a link"
                            }
                            variant="outlined"
                            value={
                              calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : ""
                            }
                            InputLabelProps={{
                              shrink: calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : "",
                            }}
                          />
                          <Box>
                            A business partner with your link will be able to
                            view and edit your calculation
                          </Box>
                          <Button
                            color="info"
                            sx={{
                              width: "40%",
                              margin: theme.spacing(2, 0, 1, "auto"),
                              display: "flex",
                              [theme.breakpoints.down("md")]: {
                                width: "100%",
                              },
                            }}
                            variant={
                              showCopiedSuccess ? "outlined" : "contained"
                            }
                            onClick={() => {
                              navigator.clipboard
                                .writeText(
                                  `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                )
                                .then(() => {
                                  setShowCopiedSuccess(true);

                                  setTimeout(function () {
                                    setShowCopiedSuccess(false);
                                  }, 2000);
                                });
                            }}
                          >
                            {showCopiedSuccess ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  padding: theme.spacing(0.5),
                                  fontWeight: 600,
                                }}
                              >
                                <Box sx={{ padding: theme.spacing(0.5) }}>
                                  <ContentCopyIcon size="lg" />
                                </Box>
                                <Box sx={{ padding: theme.spacing(0.5) }}>
                                  Copied!
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  padding: theme.spacing(0.5),
                                  fontWeight: 600,
                                }}
                              >
                                <Box sx={{ padding: theme.spacing(0.5) }}>
                                  <ContentCopyIcon size="lg" />
                                </Box>
                                <Box sx={{ padding: theme.spacing(0.5) }}>
                                  Copy
                                </Box>
                              </Box>
                            )}
                          </Button>
                        </Box>
                      </Alert>
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
            {dscr > 1.1 && (
              <Alert
                sx={{
                  margin: theme.spacing(2, 0, 0, 0),
                  width: "100%",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                  "& .MuiButtonBase-root": {
                    backgroundColor: `${theme.palette.success.main}`,
                  },
                }}
                severity="success"
                variant={"outlined"}
              >
                <>
                  <b>Looks like a great fit for our DSCR program.</b>
                  <br />
                  <br />
                  Speak with a DSCR loan expert when you get an instant quote
                </>

                <Box
                  sx={{
                    margin: theme.spacing(1, "auto", 0, "auto"),
                    display: "flex",
                    flexDirection: "column",
                    width: "33%",
                    [theme.breakpoints.down("md")]: {
                      width: "100%",
                    },
                  }}
                >
                  <Button
                    color="success"
                    sx={{
                      textDecoration: "none !important",
                      color: "white !important",
                      margin: theme.spacing(1.5, 0, 0, 0),
                      "&:hover": {
                        color: `${theme.palette.primary.contrastColor} !important`,
                        backgroundColor: `${theme.palette.success.dark} !important`,
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "auto",
                      },
                    }}
                    href={"/loans?ref=escrow-calculator-btn"}
                    onClick={() => {
                      gtag.event({ action: "dscr_calc_to_quoter_navigation" });
                    }}
                  >
                    <Box
                      sx={{
                        margin: theme.spacing(0, 1),
                        fontSize: theme.spacing(2),
                        padding: theme.spacing(0.5),
                      }}
                    >
                      {" "}
                      ⚡
                    </Box>
                    Instant quote{" "}
                  </Button>
                  <Typography
                    component="h2"
                    sx={{
                      display: "flex",
                      // width: "100% !important",
                      fontSize: theme.spacing(1.5),
                      margin: theme.spacing(0, 0, 0, "auto"),
                      [theme.breakpoints.down("md")]: {
                        display: "block",
                      },
                    }}
                  >
                    *takes 2 minutes, no credit pull
                  </Typography>
                </Box>
              </Alert>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
    </Box>
  );
}
